<template>
  <el-dialog v-model="dialogVisible" title="欢迎登录" width="420px" @close="handleClose" :close-on-click-modal="false" custom-class="login-dialog">
    <div class="dialog-content">
      <p class="dialog-subtitle">如未注册系统会帮您自动进行注册</p>
      <el-form ref="loginFormRef" :model="loginForm" :rules="rules" label-position="top">
        <el-form-item label="手机号" prop="mobile">
          <el-input v-model="loginForm.mobile" placeholder="请输入11位手机号" size="large"></el-input>
        </el-form-item>
        <el-form-item label="图形验证码" prop="verifyCode">
          <div class="verify-code-container">
            <el-input v-model="loginForm.verifyCode" placeholder="请输入图形验证码" size="large"></el-input>
            <img :src="captchaUrl" @click="refreshCaptcha" class="captcha-img" alt="验证码">
          </div>
        </el-form-item>
        <el-form-item label="短信验证码" prop="smsCode">
          <div class="sms-code-container">
            <el-input v-model="loginForm.smsCode" placeholder="请输入短信验证码" size="large"></el-input>
            <el-button @click="sendSmsCode" :disabled="smsSent" class="sms-button" text>{{ smsButtonText }}</el-button>
          </div>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleLogin" class="login-button" size="large">登 录</el-button>
        </el-form-item>
      </el-form>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, watch } from 'vue';
import { ElMessage } from 'element-plus';
import type { FormInstance, FormRules } from 'element-plus';
import { getCaptcha, sendSms, login } from '@/api/api';
import { defineProps, defineEmits } from 'vue';

const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits(['update:visible', 'login-success']);

const dialogVisible = ref(props.visible);
const loginFormRef = ref<FormInstance>();
const loginForm = reactive({
  mobile: '',
  verifyCode: '',
  smsCode: '',
});

const rules = reactive<FormRules>({
  mobile: [{ required: true, message: '请输入手机号', trigger: 'blur' }, { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }],
  verifyCode: [{ required: true, message: '请输入图形验证码', trigger: 'blur' }],
  smsCode: [{ required: true, message: '请输入短信验证码', trigger: 'blur' }],
});

const captchaUrl = ref('');
const smsSent = ref(false);
const smsButtonText = ref('获取验证码');
let timer: any = null;

watch(() => props.visible, (val) => {
  dialogVisible.value = val;
  if (val) {
    refreshCaptcha();
  } else {
    // 弹窗关闭时清空表单
    resetForm();
  }
});

const handleClose = () => {
  emit('update:visible', false);
};

// 重置表单
const resetForm = () => {
  // 清空表单数据
  loginForm.mobile = '';
  loginForm.verifyCode = '';
  loginForm.smsCode = '';
  
  // 重置表单验证
  loginFormRef.value?.resetFields();
  
  // 清除倒计时
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  smsSent.value = false;
  smsButtonText.value = '获取验证码';
  
  // 清空验证码图片
  captchaUrl.value = '';
};

const refreshCaptcha = async () => {
  try {
    const response = await getCaptcha();
    captchaUrl.value = URL.createObjectURL(response.data);
  } catch (error) {
    ElMessage.error('获取图形验证码失败');
  }
};

const sendSmsCode = async () => {
  if (!loginForm.mobile || !loginForm.verifyCode) {
    ElMessage.warning('请先输入手机号和图形验证码');
    return;
  }
  
  try {
    const response = await sendSms({ mobile: loginForm.mobile, verifyCode: loginForm.verifyCode });
    if (response.data && response.data.code === 0) {
      ElMessage.success('短信验证码已发送');
      smsSent.value = true;
      let count = 60;
      smsButtonText.value = `${count}s后重试`;
      timer = setInterval(() => {
        count--;
        if (count > 0) {
          smsButtonText.value = `${count}s后重试`;
        } else {
          clearInterval(timer);
          smsButtonText.value = '获取验证码';
          smsSent.value = false;
        }
      }, 1000);
    } else {
      ElMessage.error(response.data.msg || '发送短信验证码失败');
      refreshCaptcha();
    }
  } catch (error) {
    ElMessage.error('发送短信验证码失败');
    refreshCaptcha(); // Optionally refresh captcha on failure
  }
};

const handleLogin = () => {
  loginFormRef.value?.validate(async (valid) => {
    if (valid) {
      try {
        const response = await login({ mobile: loginForm.mobile, smsCode: loginForm.smsCode });
        // 判断code为0时才为成功
        if (response.data && response.data.code === 0) {
          // 保存登录接口返回的data参数到localStorage
          if (response.data.data) {
            localStorage.setItem('token', response.data.data);
          }
          ElMessage.success('登录成功');
          emit('login-success');
          handleClose();
        } else {
          // code不为0时，显示错误提示
          ElMessage.error(response.data?.msg || '登录失败');
        }
      } catch (error) {
        ElMessage.error('登录失败，请稍后重试');
      }
    }
  });
};

onMounted(() => {
  if (props.visible) {
    refreshCaptcha();
  }
});
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.dialog-content {
  padding: 0 20px 20px;
  position: relative;
}

.dialog-subtitle {
  font-size: 15px;
  color: #888;
  margin-bottom: 30px;
  text-align: center;
  font-weight: 400;
}

.verify-code-container, .sms-code-container {
  display: flex;
  width: 100%;
  position: relative;
}

.captcha-img {
  width: 110px;
  height: 42px;
  margin-left: 10px;
  border-radius: 6px;
  cursor: pointer;
  border: 2px solid #e4e7ed;
  transition: all 0.3s ease;
  object-fit: cover;
  background: #f5f7fa;
  box-sizing: border-box;
}

.captcha-img:hover {
  border-color: #667eea;
  transform: scale(1.02);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
}

.sms-button {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  color: #667eea;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.3s ease;
}

.sms-button:hover {
  color: #764ba2;
}

.login-button {
  width: 100%;
  margin-top: 20px;
  height: 50px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.login-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.login-button:active {
  transform: translateY(0);
}

/* 使用 :deep() 来修改 Element Plus 组件的内部样式 */
:deep(.el-dialog) {
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

:deep(.el-dialog__header) {
  padding: 32px 30px 20px;
  text-align: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-bottom: none;
  position: relative;
}

:deep(.el-dialog__header::after) {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 4px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 2px;
}

:deep(.el-dialog__title) {
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

:deep(.el-dialog__body) {
  padding: 30px 40px 40px;
  background: #ffffff;
}

:deep(.el-form-item) {
  margin-bottom: 20px;
}

:deep(.el-form-item__label) {
  font-weight: 600;
  color: #333;
  padding-bottom: 10px;
  font-size: 14px;
}

:deep(.el-input__wrapper) {
  border-radius: 8px;
  padding: 4px 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

:deep(.el-input__wrapper:hover) {
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
}

:deep(.el-input__wrapper.is-focus) {
  border-color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
}

:deep(.el-input__inner) {
  height: 34px;
  line-height: 34px;
  font-size: 14px;
  color: #333;
}

:deep(.el-input__inner::placeholder) {
  color: #c0c4cc;
  font-size: 14px;
}

:deep(.el-button--large) {
  height: 50px;
  font-size: 17px;
  font-weight: 600;
  border-radius: 10px;
}

:deep(.el-dialog__headerbtn) {
  top: 20px;
  right: 20px;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  transition: all 0.3s ease;
}

:deep(.el-dialog__headerbtn:hover) {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

:deep(.el-dialog__headerbtn .el-dialog__close) {
  font-size: 20px;
    color: #ffffff;
    font-weight: bold;
}

/* 表单验证错误样式优化 */
:deep(.el-form-item.is-error .el-input__wrapper) {
  border-color: #f56c6c;
  box-shadow: 0 0 0 2px rgba(245, 108, 108, 0.1);
}

:deep(.el-form-item__error) {
  font-size: 13px;
  padding-top: 4px;
  color: #f56c6c;
}

/* 移动端适配 */
@media (max-width: 768px) {
  :deep(.el-dialog) {
    margin: 10vh auto !important;
    max-width: 90vw;
    border-radius: 16px;
  }
  
  :deep(.el-dialog__header) {
    padding: 24px 20px 16px;
  }
  
  :deep(.el-dialog__title) {
    font-size: 20px;
  }
  
  :deep(.el-dialog__body) {
    padding: 24px 20px 30px;
  }
  
  .dialog-subtitle {
    font-size: 13px;
    margin-bottom: 20px;
  }
  
  .captcha-img {
    width: 100px;
    height: 38px;
  }
  
  .login-button {
    height: 46px;
    font-size: 16px;
  }
}
</style>
